import './Landingpage.scss';

import { ThemeProvider, createTheme } from '@mui/material';
import { deDE } from '@mui/material/locale';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';

import { faFacebookF, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { Check, Flame, Heart, Lock, Mail, ShieldCheck, UserCheck, Users, Zap } from 'lucide-react';

import Config from 'config';
import Icon from 'components/Icon';
import LoginModal from 'components/NewRegister/Login';
import ForgotPasswordModal from 'components/ForgotPasswordModal';

import heroImage from 'assets/images/landingpage/hero-fashion.png';
import profil2 from 'assets/images/landingpage/profil_2.png';
import profil3 from 'assets/images/landingpage/profil_3.png';
import profil6 from 'assets/images/landingpage/profil_6.png';
import { FORGOT_PASSWORD_PATH, FORGOT_PASSWORD_PATHH, REGISTER_USER } from '../../models/Paths';
import { DefaultTheme } from '../../theme/DefaultTheme';
import { FacebookService } from '../../services/Facebook/FacebookService';
import { GoogleService } from '../../services/Google/GoogleService';
import useLoginHandler from '../../services/Facebook/useLoginHandler';
import useGoogleLoginHandler from '../../services/Google/useGoogleLoginHandler';

export interface ILandingpageProps {}

const trustItems = [
    { icon: ShieldCheck, label: 'Diskret & sicher' },
    { icon: UserCheck, label: 'Echte Profile' },
    { icon: Zap, label: 'Schnell & einfach' },
];

const featureCards = [
    {
        icon: Heart,
        title: 'Finde, was zu dir passt',
        text: 'Entdecke Profile, die deine Vorlieben teilen und offen für das sind, was du suchst.',
        image: profil3,
        tone: 'orange',
    },
    {
        icon: Flame,
        title: 'Heißer Chat, echte Kontakte',
        text: 'Schreibe private Nachrichten und erlebe aufregende Unterhaltungen.',
        image: profil2,
        tone: 'pink',
    },
    {
        icon: Lock,
        title: 'Sicher, diskret und anonym',
        text: 'Deine Daten bleiben geschützt und deine Aktivitäten vertraulich.',
        image: '',
        tone: 'purple',
    },
];

const bulletItems = [
    'Aktive Mitglieder in deiner Nähe',
    'Private Fotos & exklusive Inhalte',
    'Direkt loslegen - ohne lange Wartezeit',
    'Für alle Vorlieben & Fantasien',
];

const bottomTrustItems = [
    {
        icon: Heart,
        title: '100% Diskretion',
        text: 'Keine öffentlichen Profile, keine Sichtbarkeit im Web.',
        tone: 'orange',
    },
    {
        icon: Lock,
        title: 'Deine Sicherheit',
        text: 'Moderne Sicherheitsstandards schützen deine Daten.',
        tone: 'pink',
    },
    {
        icon: Users,
        title: 'Für Erwachsene',
        text: 'Nur für volljährige Personen. Respektvoller Umgang garantiert.',
        tone: 'purple',
    },
];

export const Landingpage = React.memo((_props: ILandingpageProps) => {
    const history = useHistory();
    const forgotPasswordRoute = useRouteMatch({ path: FORGOT_PASSWORD_PATH, exact: true });
    const forgotPasswordLegacyRoute = useRouteMatch({ path: FORGOT_PASSWORD_PATHH, exact: true });
    const isForgotPasswordOpen = Boolean(forgotPasswordRoute || forgotPasswordLegacyRoute);

    const [loginOpen, setLoginOpen] = useState(false);
    const [, setLoading] = useState(false);

    const handleFBLogin = useLoginHandler();
    const handleGoogleLogin = useGoogleLoginHandler();
    const brandName = Config.GLOBAL_SITE_NAME.toLowerCase();
    const brandDomain = (Config.GLOBAL_SITE_DOMAIN || `${brandName}.de`).replace(/^www\./, '');
    const [brandDomainName, ...brandDomainParts] = brandDomain.split('.');
    const brandDomainEnding = brandDomainParts.length > 0 ? `.${brandDomainParts.join('.')}` : '';

    useEffect(() => {
        FacebookService.init();
        GoogleService.init();
    }, []);

    const handleGoogleClick = useCallback(() => {
        setLoading(true);
        try {
            GoogleService.login(handleGoogleLogin);
        } finally {
            setLoading(false);
        }
    }, [handleGoogleLogin]);

    const handleFacebookClick = useCallback(() => {
        setLoading(true);
        FacebookService.login(async (response) => {
            if (response.status === 'connected') {
                await handleFBLogin(response);
            } else {
                FacebookService.logout();
            }
            setLoading(false);
        });
    }, [handleFBLogin]);

    const handleEmailRegister = useCallback(() => history.push(REGISTER_USER), [history]);
    const openLogin = useCallback(() => setLoginOpen(true), []);

    return (
        <ThemeProvider theme={createTheme(DefaultTheme, deDE)}>
            <div className="pl">
                <section className="pl-hero" style={{ backgroundImage: `url(${heroImage})` }}>
                    <header className="pl-hero__nav">
                        <div className="pl-logo" aria-label={brandDomain}>
                            <span className="pl-logo__mark" aria-hidden="true">
                                <Heart />
                                <Heart />
                                <Heart />
                            </span>
                            <span className="pl-logo__name">
                                {brandDomainName || brandName}
                                <b>{brandDomainEnding || '.de'}</b>
                            </span>
                        </div>
                        <div className="pl-hero__actions">
                            <button type="button" className="pl-btn pl-btn--ghost" onClick={openLogin}>
                                Einloggen
                            </button>
                            <button type="button" className="pl-btn pl-btn--primary" onClick={handleEmailRegister}>
                                Jetzt registrieren
                            </button>
                        </div>
                    </header>

                    <div className="pl-hero__content">
                        <p className="pl-hero__age">Dating & Flirt-Chat für Erwachsene</p>
                        <h1 className="pl-hero__title">
                            Finde, was dich
                            <span>wirklich erregt</span>
                        </h1>
                        <p className="pl-hero__text">
                            {brandDomain} ist der Treffpunkt für erwachsene Menschen, die offen für echte Begegnungen und heiße Nachrichten sind.
                        </p>

                        <div className="pl-hero__trust">
                            {trustItems.map((item) => {
                                const TrustIcon = item.icon;
                                return (
                                    <div className="pl-hero__trust-item" key={item.label}>
                                        <span>
                                            <TrustIcon />
                                        </span>
                                        {item.label}
                                    </div>
                                );
                            })}
                        </div>

                        <button type="button" className="pl-hero__cta" onClick={handleEmailRegister}>
                            Kostenlos anmelden
                        </button>
                    </div>
                </section>

                <main className="pl-main">
                    <section className="pl-auth" aria-label="Schnell anmelden">
                        <p className="pl-auth__title">Schnell anmelden mit</p>
                        <div className="pl-auth__buttons">
                            <button type="button" className="pl-auth__btn pl-auth__btn--google" onClick={handleGoogleClick}>
                                <Icon icon={faGoogle} />
                                Google
                            </button>
                            <button type="button" className="pl-auth__btn pl-auth__btn--facebook" onClick={handleFacebookClick}>
                                <Icon icon={faFacebookF} />
                                Facebook
                            </button>
                            <button type="button" className="pl-auth__btn pl-auth__btn--email" onClick={handleEmailRegister}>
                                <Mail />
                                E-Mail
                            </button>
                        </div>
                        <button type="button" className="pl-auth__login" onClick={openLogin}>
                            Bereits Mitglied? <span>Jetzt einloggen</span>
                        </button>
                    </section>

                    <section className="pl-cards" aria-label="Vorteile">
                        {featureCards.map((card) => {
                            const FeatureIcon = card.icon;
                            return (
                                <article className={`pl-card pl-card--${card.tone}`} key={card.title}>
                                    {card.image ? <img src={card.image} alt="" loading="lazy" /> : <Lock className="pl-card__lock-bg" aria-hidden="true" />}
                                    <div className="pl-card__shade" />
                                    <span className="pl-card__icon">
                                        <FeatureIcon />
                                    </span>
                                    <div className="pl-card__content">
                                        <h2>{card.title}</h2>
                                        <p>{card.text}</p>
                                    </div>
                                </article>
                            );
                        })}
                    </section>
                </main>

                <section className="pl-chat">
                    <div className="pl-chat__inner">
                        <div className="pl-phone" aria-label="Chat Vorschau">
                            <div className="pl-phone__speaker" />
                            <div className="pl-phone__top">
                                <span className="pl-phone__back">‹</span>
                                <img src={profil2} alt="" />
                                <div>
                                    <strong>Lara</strong>
                                    <small>Online</small>
                                </div>
                                <span className="pl-phone__menu">...</span>
                            </div>
                            <img className="pl-phone__photo" src={profil2} alt="" loading="lazy" />
                            <div className="pl-phone__bubble pl-phone__bubble--light">
                                Hi du...<br />
                                Lust auf ein heißes Gespräch?
                            </div>
                            <div className="pl-phone__bubble pl-phone__bubble--dark">Na klar! Woran denkst du gerade?</div>
                        </div>

                        <div className="pl-chat__copy">
                            <p className="pl-chat__eyebrow">Live Flirt Chat</p>
                            <h2>Erlebe echte Spannung</h2>
                            <p className="pl-chat__text">Unsere Community ist aktiv und wartet auf dich. Starte jetzt deinen ersten Flirt!</p>
                            <ul className="pl-chat__list">
                                {bulletItems.map((item) => (
                                    <li key={item}>
                                        <Check /> {item}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="pl-stamp" aria-label="Verifizierte Profile">
                            <div className="pl-stamp__circle">
                                <Check />
                                <span>Verifizierte Profile</span>
                            </div>
                            <p>Nur echte Mitglieder für echte Erlebnisse.</p>
                        </div>
                    </div>
                </section>

                <section className="pl-bottom-trust" aria-label="Sicherheit und Diskretion">
                    {bottomTrustItems.map((item) => {
                        const TrustIcon = item.icon;
                        return (
                            <article className={`pl-bottom-trust__item pl-bottom-trust__item--${item.tone}`} key={item.title}>
                                <TrustIcon />
                                <div>
                                    <h3>{item.title}</h3>
                                    <p>{item.text}</p>
                                </div>
                            </article>
                        );
                    })}
                </section>

                <footer className="pl-footer">
                    <nav className="pl-footer__links" aria-label="Rechtliches">
                        <Link to="/support">Kontakt</Link>
                        <Link to="/impressum">Impressum</Link>
                        <Link to="/datenschutz">Datenschutz</Link>
                        <Link to="/agb">AGB</Link>
                        <Link to="/content-guidelines">Content &amp; Community Guidelines</Link>
                    </nav>
                    <p className="pl-footer__legal">
                        Diese Webseite darf ausschlie&szlig;lich von Personen genutzt werden, die mindestens 18 Jahre alt sind. Die Plattform stellt
                        einen virtuellen Chat-Dienst f&uuml;r unterhaltsame, flirtende und fantasievolle Gespr&auml;che zur Verf&uuml;gung. Hierbei
                        werden fiktive Profile verwendet, mit denen keine pers&ouml;nlichen oder realen Treffen m&ouml;glich sind. Die Kosten je
                        Nachricht k&ouml;nnen je nach gew&auml;hlter Zahlungsart unterschiedlich ausfallen. Bitte informieren Sie sich vor der Nutzung
                        der Webseite &uuml;ber die geltenden Nutzungsbedingungen und Datenschutzbestimmungen.
                    </p>
                </footer>

                <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
                <ForgotPasswordModal open={isForgotPasswordOpen} />
            </div>
        </ThemeProvider>
    );
});

export default Landingpage;
